import { useId } from "react";
import type { ReactElement } from "react";
import type { LayerVisibility } from "../../state/layerState";
import { InspectorRow } from "./InspectorRow";
import styles from "./BaseLayersCard.module.css";

/** Keys mirrored from the React Base card — aligned with migrated visibility toggles. */
export const BASE_VISIBILITY_KEYS = [
  "parcelBoundary",
  "lotDimensions",
  "roadContext",
  "buildingFootprints",
  "satelliteImagery",
  "landcoverOverlay",
  "drivewayCandidates"
] as const;

export type BaseVisibilityKey = (typeof BASE_VISIBILITY_KEYS)[number];

const LABELS: Record<BaseVisibilityKey, string> = {
  parcelBoundary: "Parcel boundary",
  lotDimensions: "Lot dimensions",
  roadContext: "Roads",
  buildingFootprints: "Existing structures",
  satelliteImagery: "Satellite imagery",
  landcoverOverlay: "Land cover (NLCD)",
  drivewayCandidates: "Driveway candidates"
};

/**
 * Where a card body is hosted. Cards are body-only: the host (rail flyout
 * on desktop, bottom sheet on handheld) renders the title row. `drawer`
 * switches rows to the taller handset sizing.
 */
export type InspectorPresentation = "inspector" | "drawer";

export interface BaseLayersCardProps {
  visibility: LayerVisibility;
  onToggle: (key: BaseVisibilityKey, value: boolean) => void;
  presentation?: InspectorPresentation;
  /** Visibility keys whose value differs from the viewer default (accent dot). */
  modifiedKeys?: ReadonlySet<string>;
  /**
   * Per-layer availability notes ("No footprints found for this site").
   * A layer with a note still toggles — it just has nothing to draw yet —
   * so the row explains why flipping it appears to do nothing.
   */
  layerNotes?: Partial<Record<BaseVisibilityKey, string>>;
}

export function BaseLayersCard({
  visibility,
  onToggle,
  modifiedKeys,
  layerNotes
}: BaseLayersCardProps): ReactElement {
  const reactId = useId();

  return (
    <div>
      {BASE_VISIBILITY_KEYS.map((key) => {
        const note = layerNotes?.[key];
        return (
          <div key={key}>
            <InspectorRow
              label={LABELS[key]}
              labelWeight="normal"
              checkboxId={`${reactId}-${key}`}
              checked={visibility[key]}
              modified={modifiedKeys?.has(key) === true}
              onCheckedChange={(v) => {
                onToggle(key, v);
              }}
            />
            {note ? <p className={styles.layerNote}>{note}</p> : null}
          </div>
        );
      })}
    </div>
  );
}
